import { useUnit } from 'effector-react';
import { Send } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  $locale,
  $preferences,
  $theme,
  loadPreferencesFx,
  localeChanged,
  themeChanged,
} from '../../entities/preferences';
import { toastShown } from '../../entities/toast';
import { api, unwrap, type Schemas } from '../../shared/api/client';
import { parseDecimal } from '../../shared/lib/decimal';
import { formatDateTime, formatNumber, formatRelative } from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { LANGUAGE_NAME, LOCALES } from '../../shared/lib/locale';
import {
  Button,
  CommitField,
  Field,
  Pill,
  Section,
  Segmented,
  Select,
  StatusDot,
  Switch,
  Tooltip,
} from '../../shared/ui';
import { AppShell } from '../../widgets/app-shell';
import { settingChanged } from './model';

type Prefs = Schemas['Preferences-Output'];

export function SettingsPage() {
  const { t } = useTranslation();
  const [prefs, load] = useUnit([$preferences, loadPreferencesFx]);
  useEffect(() => {
    void load().catch(() => undefined);
  }, [load]);
  return (
    <AppShell page="settings" crumbs={[t('nav.settings')]}>
      <div className="mx-auto w-full max-w-3xl space-y-4 px-6 py-6 max-sm:px-4">
        <h1 className="text-lg font-semibold">{t('nav.settings')}</h1>
        <div className="rounded-lg border border-border-subtle bg-bg-1">
          {prefs && <Notifications prefs={prefs} />}
          {prefs && <Defaults prefs={prefs} />}
          <Appearance />
          {prefs && <Region prefs={prefs} />}
          <System />
        </div>
      </div>
    </AppShell>
  );
}

function useSave(): (patch: Partial<Prefs>) => void {
  return useUnit(settingChanged);
}

function Notifications({ prefs }: { prefs: Prefs }) {
  const { t } = useTranslation();
  const save = useSave();
  const toast = useUnit(toastShown);
  const [emailError, setEmailError] = useState<string | null>(null);
  return (
    <Section
      title={t('settings.notifications')}
      actions={
        <Button
          size="sm"
          leadingIcon={<Send />}
          disabled={!prefs.notify_email}
          onClick={() => {
            unwrap(api.POST('/api/v1/settings/test-email'))
              .then(() => {
                toast({
                  message: t('settings.testSent', { email: prefs.notify_email }),
                  tone: 'neutral',
                });
              })
              .catch((error: unknown) => {
                toast({
                  message: error instanceof Error ? error.message : String(error),
                  tone: 'error',
                });
              });
          }}
        >
          {t('settings.sendTest')}
        </Button>
      }
    >
      <Field
        label={t('settings.email')}
        hint={t('settings.emailHint')}
        error={emailError ?? undefined}
      >
        {({ id, describedBy }) => (
          <CommitField
            id={id}
            type="email"
            inputMode="email"
            value={prefs.notify_email}
            aria-describedby={describedBy}
            validate={(text) =>
              text.trim() === '' || /^[^@\s]+@[^@\s]+$/.test(text.trim())
                ? null
                : t('settings.emailInvalid')
            }
            onErrorChange={setEmailError}
            onCommit={(text) => {
              save({ notify_email: text.trim() });
            }}
          />
        )}
      </Field>
      <ToggleRow
        label={t('settings.marketOpenEmail')}
        hint={t('settings.marketOpenHint')}
        checked={prefs.market_open_email}
        onChange={(value) => {
          save({ market_open_email: value });
        }}
      />
      <ToggleRow
        label={t('settings.regimeEmails')}
        hint={t('settings.regimeHint')}
        checked={prefs.regime_emails}
        onChange={(value) => {
          save({ regime_emails: value });
        }}
      />
      <Field label={t('settings.reminderDays')} hint={t('settings.reminderHint')}>
        {({ id, describedBy }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            value={String(prefs.reminder_days)}
            options={[1, 2, 3, 5, 10].map((n) => ({
              value: String(n),
              label: t('settings.sessions', { count: n }),
            }))}
            onValueChange={(value) => {
              save({ reminder_days: Number(value) });
            }}
          />
        )}
      </Field>
    </Section>
  );
}

function ToggleRow({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <Field label={label} hint={hint}>
      {({ id, describedBy }) => (
        <div className="flex h-7 items-center">
          <Switch
            id={id}
            aria-describedby={describedBy}
            checked={checked}
            onCheckedChange={onChange}
          />
        </div>
      )}
    </Field>
  );
}

function Defaults({ prefs }: { prefs: Prefs }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const save = useSave();
  const show = (value: string): string => (locale === 'bg' ? value.replace('.', ',') : value);
  const decimal = (
    field: 'default_target_pct' | 'default_step_pct' | 'default_multiplier',
    label: string,
    unit: string,
  ) => (
    <Field label={label}>
      {({ id, describedBy }) => (
        <CommitField
          id={id}
          numeric
          unit={unit}
          inputMode="decimal"
          aria-describedby={describedBy}
          value={show(prefs[field])}
          validate={(text) => (parseDecimal(text, locale) === null ? t('form.notANumber') : null)}
          onCommit={(text) => {
            const parsed = parseDecimal(text, locale);
            if (parsed !== null) save({ [field]: parsed });
          }}
        />
      )}
    </Field>
  );
  return (
    <Section title={t('settings.defaults')}>
      {decimal('default_target_pct', t('openPosition.target'), '%')}
      {decimal('default_step_pct', t('openPosition.step'), '%')}
      {decimal('default_multiplier', t('openPosition.multiplier'), '×')}
      <Field label={t('openPosition.levels')}>
        {({ id }) => (
          <Select
            id={id}
            value={String(prefs.default_levels)}
            options={[1, 2, 3, 4, 5, 6].map((n) => ({ value: String(n), label: String(n) }))}
            onValueChange={(value) => {
              save({ default_levels: Number(value) });
            }}
          />
        )}
      </Field>
    </Section>
  );
}

function Appearance() {
  const { t } = useTranslation();
  const [theme, setTheme, locale, setLocale] = useUnit([
    $theme,
    themeChanged,
    $locale,
    localeChanged,
  ]);
  return (
    <Section title={t('settings.appearance')}>
      <Field label={t('preferences.theme')}>
        {() => (
          <Segmented
            label={t('preferences.theme')}
            value={theme}
            onValueChange={setTheme}
            options={[
              { value: 'system', label: t('preferences.themes.system') },
              { value: 'dark', label: t('preferences.themes.dark') },
              { value: 'light', label: t('preferences.themes.light') },
            ]}
          />
        )}
      </Field>
      <Field label={t('preferences.language')} hint={t('settings.languageHint')}>
        {({ id, describedBy }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            value={locale}
            options={LOCALES.map((l) => ({ value: l, label: LANGUAGE_NAME[l] }))}
            onValueChange={setLocale}
          />
        )}
      </Field>
    </Section>
  );
}

function zones(current: string): string[] {
  const all =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  const europe = all.filter((z) => z.startsWith('Europe/'));
  return [...new Set(['UTC', current, ...europe])].sort();
}

function Region({ prefs }: { prefs: Prefs }) {
  const { t } = useTranslation();
  const save = useSave();
  return (
    <Section title={t('settings.region')}>
      <Field label={t('settings.timezone')} hint={t('settings.timezoneHint')}>
        {({ id, describedBy }) => (
          <Select
            id={id}
            aria-describedby={describedBy}
            value={prefs.timezone}
            options={zones(prefs.timezone).map((z) => ({
              value: z,
              label: z.replaceAll('_', ' '),
            }))}
            onValueChange={(value) => {
              save({ timezone: value });
            }}
          />
        )}
      </Field>
    </Section>
  );
}

function System() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [system, setSystem] = useState<Schemas['SystemOut'] | null>(null);
  useEffect(() => {
    unwrap(api.GET('/api/v1/settings/system'))
      .then(setSystem)
      .catch(() => undefined);
  }, []);
  if (!system) return null;
  const now = new Date();
  return (
    <Section title={t('settings.system')}>
      <Field label={t('settings.dataFreshness')}>
        {() => (
          <p className="pt-1.5 text-sm">
            {system.newest_quote_at
              ? t('settings.lastQuote', {
                  time: formatRelative(system.newest_quote_at, now, locale),
                })
              : t('settings.noQuotes')}
            <span className="text-text-3">{` · ${system.market_provider}`}</span>
          </p>
        )}
      </Field>
      <Field label={t('settings.emailDelivery')}>
        {() => (
          <p className="pt-1.5 text-sm">
            {t('settings.outbox', {
              pending: formatNumber(system.outbox_pending, locale),
              failed: formatNumber(system.outbox_failed, locale),
            })}
            <span className="text-text-3">{` · ${system.email_provider}`}</span>
          </p>
        )}
      </Field>
      <ul className="divide-y divide-border-subtle rounded-md border border-border-subtle">
        {system.jobs.map((job) => (
          <li
            key={job.job}
            className="flex min-h-8 flex-wrap items-center gap-x-3 gap-y-1 px-3 py-1.5 text-sm"
          >
            <span className="w-36 shrink-0 font-medium">{job.job}</span>
            {job.last_status ? (
              <Pill
                icon={
                  <StatusDot
                    status={
                      job.last_status === 'ok'
                        ? 'good'
                        : job.last_status === 'failed'
                          ? 'critical'
                          : 'warning'
                    }
                  />
                }
              >
                {t(`settings.jobStatus.${job.last_status}`)}
              </Pill>
            ) : (
              <span className="text-text-3">{t('settings.neverRan')}</span>
            )}
            <span className="min-w-0 flex-1 truncate text-xs text-text-3">
              {job.last_message ?? ''}
            </span>
            {job.last_run_at && (
              <Tooltip content={formatDateTime(job.last_run_at, locale)}>
                <span className="text-xs text-text-3">
                  {formatRelative(job.last_run_at, now, locale)}
                </span>
              </Tooltip>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
}
